/**
 * Single source of truth for session-expiry handling.
 *
 * Triggered from two places:
 *   - V2 global axios interceptor (Route/index.jsx) — for legacy pages
 *   - V3 axios instance interceptor (hooks/useApi.js) — for V3 pages
 *
 * Idempotent: concurrent failing requests don't stack alerts/redirects.
 * Skips alert + redirect when already on /login (avoids loops on bad-password
 * attempts that surface as 401/403 from the auth endpoint itself).
 */

let triggered = false;

// Auth-related localStorage keys cleared on session expiry. Mirrors V2's
// historical key set (Route/index.jsx) plus V3 additions (refresh_token,
// concessionaireId, propertyAccess, …). Conservative — leaves user UI prefs
// (theme, layout) intact so they survive re-login.
const AUTH_KEYS = [
    'sessiontoken',
    'refresh_token',
    'token',
    'authenticated',
    'login',
    'profileURL',
    'auth0_profile',
    'loginName',
    'loginId',
    'usertype',
    'groupId',
    'userid',
    'userrole',
    'username',
    'firstName',
    'concessionaireId',
    'concessionaireName',
    'propertyAccess',
    'dataMode',
    'auditRequired',
    'auditTolerance',
    'accessDashboard', 'accessAnalytics', 'accessReports', 'accessRevenueReports',
    'accessDataGrid', 'accessGeneralSettings', 'manageGeneralSettings',
    'accessTenants', 'manageTenants', 'manageConfigurations', 'manageUsers',
    'manageUserProfile', 'accessLogs', 'manageScheduleReports',
    'tenantPortalManage', 'manageLeaseDetails', 'accessLicenses', 'manageLicenses',
    'dataDeletePermission',
];

function isOnLogin() {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname || '';
    return path.endsWith('/login');
}

/**
 * Tear down the session and redirect to /login.
 * Safe to call from anywhere; subsequent calls in the same tab are no-ops.
 *
 * @param {object}  [opts]
 * @param {boolean} [opts.silent=false]   suppress the alert (rare; V2 path uses the alert).
 * @param {string}  [opts.reason]         'idle' → inactivity message; else generic session-ended.
 */
export function handleSessionExpired(opts = {}) {
    if (triggered) return;
    if (typeof window === 'undefined') return;
    if (isOnLogin()) return;

    triggered = true;
    const silent = !!opts.silent;

    // Tear down WS so it doesn't reconnect with a dead JWT. Lazy-required to
    // avoid a circular import at module load.
    try {
        const { disconnectSocket } = require('./useV3Socket');
        if (typeof disconnectSocket === 'function') disconnectSocket();
    } catch (_) { /* socket hook may not be loaded yet */ }

    try {
        AUTH_KEYS.forEach(k => localStorage.removeItem(k));
    } catch (_) { /* private browsing / quota errors */ }

    if (!silent) {
        const msg = opts.reason === 'idle'
            ? 'You have been signed out due to inactivity. Please log in again.'
            : 'Session has ended. Please log in again.';
        try { alert(msg); } catch (_) { /* noop */ }
    }

    // Reference `process.env.PUBLIC_URL` DIRECTLY. Do not add a
    // `typeof process !== 'undefined'` guard around it.
    //
    // Webpack's DefinePlugin substitutes the exact token `process.env` at build
    // time; it never defines `process` itself, and Webpack 5 dropped the Node
    // globals polyfill. So a guard compiles to:
    //     typeof process !== 'undefined' && ({"PUBLIC_URL":"/v3.0",...})
    // where the left side evaluates at RUNTIME, is false in every browser, and
    // short-circuits the whole expression to ''. The guard silently defeats the
    // substitution it was meant to protect.
    //
    // That shipped: on the /v3.0 sub-path deploy this redirected to /login
    // instead of /v3.0/login, so every idle logout landed on a 404 and the user
    // could not get back to the login screen. The other eight redirect sites
    // use this bare form and were unaffected.
    const base = process.env.PUBLIC_URL || '';
    window.location.href = `${base}/login`;
}

/**
 * Suppress the session-expiry handler for an intentional, user-initiated logout.
 * Clearing auth keys on manual sign-out would otherwise make in-flight requests
 * 401 and surface the "Session has ended" alert. Latch it closed first so those
 * races become no-ops — the logout flow handles its own teardown + redirect.
 */
export function suppressSessionExpiry() {
    triggered = true;
}

/**
 * Test/dev helper — reset the latch. Not used in production code paths.
 */
export function _resetSessionExpiredFlag() {
    triggered = false;
}

export default handleSessionExpired;
